import api from "../services/authService";

/**
 * Work Items API client — the NEW Salesforce org's attorney→paralegal
 * baton, surfaced in WHG Docs (backend dms_backend #2049; org-side
 * contract in whg-salesforce docs/dms-work-items-doorway.md).
 *
 * The inbox's create/attach calls live in service/inbox.js because they
 * are document-scoped. This module is the matter-scoped read/act
 * surface behind the matter page's Work panel: one matter's work in
 * lanes, one item's thread, and the verbs that move the baton. The
 * backend resolves the acting user by email and the Salesforce doorway
 * enforces whose hands may do what — the FE only offers verbs the
 * payload's `can*` flags say this user has.
 */

/**
 * GET /api/matters/<matter_id>/work-items/
 *
 * `{ enabled, matter_found, work_items, closed_work_items, checklist,
 *    reminders, waiting_outside }`. Work items carry staleness
 * (`staleness`/`staleLabel`/`ageDays`), court (`courtLabel`,
 * `ownerName`), and per-actor verbs (`canReturn`, `canSendBack`,
 * `canClose`). `matter_found: false` = not migrated yet — a normal
 * answer, not an error.
 */
export const getMatterWorkItems = (matterId) => {
  if (matterId == null) return Promise.reject(new Error("matterId required"));
  return api.get(`/matters/${matterId}/work-items/`);
};

/**
 * GET /api/work-items/<work_item_id>/thread/
 *
 * `{ entries: [{ author, whenLabel, createdAt, body }] }` — comments
 * made through WHG Docs carry the human's name, not the integration
 * user's.
 */
export const getWorkItemThread = (workItemId) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  return api.get(`/work-items/${workItemId}/thread/`);
};

/**
 * POST /api/work-items/<work_item_id>/act/
 *
 * Move the baton as the signed-in user: `action` is one of
 * return | sendBack | close | comment; `note` is HTML (required for
 * everything but close); `recipient_id` overrides send-back's default
 * (the last assignee). Refusals come back with an honest `detail`.
 */
export const actOnWorkItem = (
  workItemId,
  { action, note, recipient_id } = {}
) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  if (!action) return Promise.reject(new Error("action required"));
  return api.post(`/work-items/${workItemId}/act/`, {
    action,
    ...(note ? { note } : {}),
    ...(recipient_id ? { recipient_id } : {}),
  });
};

/* ---- Work Items v2 (11/2): routes, Following, attachment lists ---- */

/**
 * `{"action": "sendBackHop", "note"}` — on a route, the current holder
 * (stop 2 or later) sends the item back one stop. Note required.
 */
export const sendWorkItemBackAStop = (workItemId, { note } = {}) =>
  actOnWorkItem(workItemId, { action: "sendBackHop", note });

/** Keep myself in the loop on an item I neither hold nor requested. */
export const followWorkItem = (workItemId) =>
  actOnWorkItem(workItemId, { action: "follow" });

export const unfollowWorkItem = (workItemId) =>
  actOnWorkItem(workItemId, { action: "unfollow" });

/** cc more people; each gets one bell. `followerIds` are new-org user ids. */
export const addWorkItemFollowers = (
  workItemId,
  { followerIds = [], followerEmails = [] } = {}
) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "addFollowers",
    ...(followerIds.length ? { follower_ids: followerIds } : {}),
    ...(followerEmails.length ? { follower_emails: followerEmails } : {}),
  });
};

/** Take one document off the item (whoever attached it, the holder, or the requester). */
export const detachWorkItemDocument = (workItemId, { attachmentId } = {}) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  if (!attachmentId) return Promise.reject(new Error("attachmentId required"));
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "detach",
    attachment_id: attachmentId,
  });
};

/**
 * POST /api/work-items/<work_item_id>/act/ {"action": "edit", ...}
 *
 * The sender rewords an open item: the whole new title and instruction
 * (HTML), and the due date (`YYYY-MM-DD`; blank clears it). The doorway
 * offers this only where the payload says `canEdit`.
 */
export const editWorkItem = (workItemId, { subject, instruction, dueDate } = {}) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "edit",
    subject,
    instruction,
    due_date: dueDate || "",
  });
};

/**
 * POST /api/work-items/tasks/<task_id>/complete/
 *
 * Complete one checklist or reminder task. `outcome`
 * (Successful | Unsuccessful | Skipped) only where the task demands
 * one (`needsOutcome`); stage-engine dependents spawn org-side.
 */
export const completeWorkTask = (taskId, { outcome } = {}) => {
  if (taskId == null) return Promise.reject(new Error("taskId required"));
  return api.post(`/work-items/tasks/${taskId}/complete/`, {
    ...(outcome ? { outcome } : {}),
  });
};

/**
 * GET /api/matters/<matter_id>/work-item-context/
 *
 * The matter-anchored twin of the inbox's work-item context: is the
 * feature armed, has this matter migrated, and who can an instruction
 * be sent to (matter team, case manager preselected). Feeds the matter
 * page's Create Work Item form.
 */
export const getMatterWorkItemContext = (matterId) => {
  if (matterId == null) return Promise.reject(new Error("matterId required"));
  return api.get(`/matters/${matterId}/work-item-context/`);
};

/**
 * POST /api/matters/<matter_id>/work-items/create/
 *
 * Create one work item on this matter — no document attached, the
 * matter page's Create Work Item. Same body as the inbox create:
 * `{ assignee_id, type, subject, instruction }`.
 */
export const createMatterWorkItem = (matterId, payload) => {
  if (matterId == null) return Promise.reject(new Error("matterId required"));
  return api.post(`/matters/${matterId}/work-items/create/`, payload);
};

/**
 * POST /api/work-items/<work_item_id>/act/ {"action": "snooze", ...}
 *
 * The holder pauses the item until a date, with a mandatory note
 * (Matthew, 9/4 — the snooze/blocked/outside/chain build). Home folds
 * it into "Snoozed" until then; it comes back active on its own once
 * the date passes (defended client-side too — see `activationOf` in
 * Home/MyWork/plate.js). `until` is `YYYY-MM-DD`.
 */
export const snoozeWorkItem = (workItemId, { until, note } = {}) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  if (!until) return Promise.reject(new Error("until required"));
  if (!note) return Promise.reject(new Error("note required"));
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "snooze", until, note,
  });
};

/**
 * POST /api/work-items/<work_item_id>/act/ {"action": "unsnooze", ...}
 *
 * Bring a snoozed item back to active early. Note optional.
 */
export const unsnoozeWorkItem = (workItemId, { note } = {}) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "unsnooze",
    ...(note ? { note } : {}),
  });
};

/**
 * POST /api/work-items/<work_item_id>/act/ {"action": "logRequest", ...}
 *
 * "I've asked…" — the holder logs that the ask left the building; a
 * linked open Request appears and the item reads as "outside"
 * everywhere until it's answered. The request TYPE is derived
 * org-side from the item's own type (Order Exam → IME / Nexus
 * Opinion, Request Records → Medical Records, Get Statement → Lay /
 * Buddy Statement, else Other) — never sent from here. Every field
 * is optional; `channel` is one of Fax | Email | Mail | Phone |
 * Portal, `expectedBy` is `YYYY-MM-DD`.
 */
export const logWorkItemRequest = (
  workItemId, { requestedFrom, channel, expectedBy } = {},
) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "logRequest",
    ...(requestedFrom ? { requested_from: requestedFrom } : {}),
    ...(channel ? { channel } : {}),
    ...(expectedBy ? { expected_by: expectedBy } : {}),
  });
};

/**
 * POST /api/work-items/<work_item_id>/act/ {"action": "logStep", ...}
 *
 * Mark what has happened on an item without moving the baton
 * (Matthew, 9/9: "a sub-chip of some sort in the work item —
 * 'Requested' and the circumstances"). `step` is one of the doorway's
 * step names (Called client | Left voicemail | Emailed client |
 * Texted client | Discussed with client | Requested | Follow-up |
 * Other); `occurredOn` / `followUpOn` are `YYYY-MM-DD`;
 * `counterparty` is who it involved (vendor, person); `channel` only
 * matters for Requested; `note` is HTML. The org writes the stepped
 * thread entry, restarts the court clock, and sets the follow-up.
 * "Requested" on an outside-request type IS "I've asked…" — one
 * chip, one Request, never two.
 */
export const logWorkItemStep = (
  workItemId,
  { step, occurredOn, counterparty, followUpOn, channel, note } = {},
) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  if (!step) return Promise.reject(new Error("step required"));
  return api.post(`/work-items/${workItemId}/act/`, {
    action: "logStep",
    step,
    ...(occurredOn ? { occurred_on: occurredOn } : {}),
    ...(counterparty ? { counterparty } : {}),
    ...(followUpOn ? { follow_up_on: followUpOn } : {}),
    ...(channel ? { channel } : {}),
    ...(note ? { note } : {}),
  });
};

/**
 * GET /api/matters/<matter_id>/work-items/documents/?q=
 *
 * The picker behind "Attach document" on a card: this matter's
 * documents, newest first, name-filtered by `q`. Slim rows —
 * `{ id, name, created_at, folder_name }`.
 */
export const getMatterWorkItemDocuments = (matterId, { q } = {}) => {
  if (matterId == null) return Promise.reject(new Error("matterId required"));
  return api.get(`/matters/${matterId}/work-items/documents/`, {
    params: q ? { q } : {},
  });
};

/**
 * POST /api/work-items/<work_item_id>/attach/
 *
 * The belt entered from the item's side: pin one of the matter's
 * documents to a known work item. Same move as the inbox's Attach
 * button — the link lands in the thread, the item remembers its
 * document, and a Sent item volleys back to the requester
 * (`volleyedToRequester` in the reply). `note` is HTML, optional.
 */
export const attachWorkItemDocument = (
  workItemId,
  { documentId, note, andReturn = true, role } = {}
) => {
  if (workItemId == null) {
    return Promise.reject(new Error("workItemId required"));
  }
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  // Work Items v2 (Matthew, 11/2): `andReturn: false` is a plain attach —
  // the document joins the item and the baton stays put. `true` is the
  // one-click Attach & return that was the only move before.
  return api.post(`/work-items/${workItemId}/attach/`, {
    document_id: String(documentId),
    return: andReturn !== false,
    ...(role ? { role } : {}),
    ...(note ? { note } : {}),
  });
};
